import { useState } from 'react';
import { Upload, Image as ImageIcon, Music, Video, Trash2, Eye, X } from 'lucide-react';
import { uploadImage, deleteImage } from '../services/imageUploadService';

interface MediaItem {
  id: string;
  url: string;
  title: string;
  description?: string;
  type: 'image' | 'podcast' | 'video';
  uploadedAt?: string;
}

interface ExperienceMediaManagerProps {
  experienceId: string;
  media: {
    images?: MediaItem[];
    podcasts?: MediaItem[];
    videos?: MediaItem[];
  };
  onMediaUpdate: (media: {
    images?: MediaItem[];
    podcasts?: MediaItem[];
    videos?: MediaItem[];
  }) => void;
}

const ExperienceMediaManager = ({ experienceId, media, onMediaUpdate }: ExperienceMediaManagerProps) => {
  const [activeTab, setActiveTab] = useState<'images' | 'podcasts' | 'videos'>('images');
  const [isUploading, setIsUploading] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'podcast' | 'video') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!newItemTitle.trim()) {
      alert('Inserisci un titolo per il contenuto');
      return;
    }

    try {
      setIsUploading(true);
      const path = `experiences/${experienceId}/${type}s/${file.name}`;
      const url = await uploadImage(file, path);

      const newItem: MediaItem = {
        id: Date.now().toString(),
        url,
        title: newItemTitle,
        description: newItemDescription || undefined,
        type,
        uploadedAt: new Date().toISOString()
      };

      const currentItems = media[`${type}s` as keyof typeof media] || [];
      const updatedMedia = {
        ...media,
        [`${type}s`]: [...currentItems, newItem]
      };

      onMediaUpdate(updatedMedia);
      setNewItemTitle('');
      setNewItemDescription('');
      event.target.value = ''; // Reset input
      alert(`${type === 'image' ? 'Immagine' : type === 'podcast' ? 'Podcast' : 'Video'} caricato con successo!`);
    } catch (error) {
      console.error('Errore upload:', error);
      alert('Errore durante il caricamento del file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (itemId: string, type: 'images' | 'podcasts' | 'videos') => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contenuto?')) {
      return;
    }

    try {
      const currentItems = media[type] || [];
      const item = currentItems.find(i => i.id === itemId);
      
      if (item) {
        // Prova a eliminare da Firebase Storage
        try {
          await deleteImage(item.url);
        } catch (err) {
          console.warn('Impossibile eliminare il file da Storage:', err);
        }
      }

      const updatedItems = currentItems.filter(i => i.id !== itemId);
      const updatedMedia = {
        ...media,
        [type]: updatedItems
      };

      onMediaUpdate(updatedMedia);
    } catch (error) {
      console.error('Errore eliminazione:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  const renderMediaItems = (items: MediaItem[] | undefined, type: 'images' | 'podcasts' | 'videos') => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Nessun contenuto caricato</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Preview */}
            <div className="relative bg-gray-100 h-48 flex items-center justify-center">
              {type === 'images' && (
                <img 
                  src={item.url} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder-image.jpg';
                  }}
                />
              )}
              {type === 'podcasts' && (
                <div className="flex flex-col items-center">
                  <Music size={48} className="text-gray-400 mb-2" />
                  <audio controls className="w-full px-4">
                    <source src={item.url} />
                    Il tuo browser non supporta l'elemento audio.
                  </audio>
                </div>
              )}
              {type === 'videos' && (
                <video controls className="w-full h-full">
                  <source src={item.url} />
                  Il tuo browser non supporta l'elemento video.
                </video>
              )}
              
              {/* Actions */}
              <div className="absolute top-2 right-2 flex space-x-1">
                <button
                  onClick={() => setPreviewUrl(item.url)}
                  className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                  title="Anteprima"
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, type)}
                  className="p-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition-colors"
                  title="Elimina"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="p-3">
              <h4 className="font-medium text-gray-800 mb-1">{item.title}</h4>
              {item.description && (
                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              )}
              {item.uploadedAt && (
                <p className="text-xs text-gray-400">
                  {new Date(item.uploadedAt).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Gestione Contenuti Media</h3>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('images')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'images'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <ImageIcon size={16} className="inline mr-2" />
          Immagini ({media.images?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('podcasts')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'podcasts'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Music size={16} className="inline mr-2" />
          Podcast ({media.podcasts?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'videos'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Video size={16} className="inline mr-2" />
          Video ({media.videos?.length || 0})
        </button>
      </div>

      {/* Upload Form */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h4 className="font-medium text-gray-800 mb-3">
          Carica nuovo {activeTab === 'images' ? 'immagine' : activeTab === 'podcasts' ? 'podcast' : 'video'}
        </h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
            <input
              type="text"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Inserisci un titolo descrittivo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
            <textarea
              value={newItemDescription}
              onChange={(e) => setNewItemDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Descrizione opzionale"
            />
          </div>
          <div>
            <label className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-primary/90 transition-colors">
              <Upload size={16} />
              <span>{isUploading ? 'Caricamento...' : 'Scegli File'}</span>
              <input
                type="file"
                accept={
                  activeTab === 'images' ? 'image/*' :
                  activeTab === 'podcasts' ? 'audio/*' :
                  'video/*'
                }
                onChange={(e) => handleFileUpload(e, activeTab === 'images' ? 'image' : activeTab === 'podcasts' ? 'podcast' : 'video')}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Media Items */}
      {activeTab === 'images' && renderMediaItems(media.images, 'images')}
      {activeTab === 'podcasts' && renderMediaItems(media.podcasts, 'podcasts')}
      {activeTab === 'videos' && renderMediaItems(media.videos, 'videos')}

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewUrl(null)}
              className="absolute -top-10 right-0 p-2 bg-white rounded-lg shadow-md hover:bg-gray-100"
            >
              <X size={24} />
            </button>
            {activeTab === 'images' && (
              <img src={previewUrl} alt="Preview" className="w-full rounded-lg" />
            )}
            {activeTab === 'podcasts' && (
              <audio controls autoPlay className="w-full">
                <source src={previewUrl} />
              </audio>
            )}
            {activeTab === 'videos' && (
              <video controls autoPlay className="w-full rounded-lg">
                <source src={previewUrl} />
              </video>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceMediaManager;
